import {Record, List, OrderedSet, Map} from 'immutable';

import {type Plan} from 'web-app/react/entities/plans/model';
import {type Omit} from 'web-app/util/typescript';
import {nameofFactory} from 'web-app/util/typescript';
import {SortDirection} from 'web-app/util/sorting';
import {type GetBillingProfilesByIdsQuery} from 'web-app/react/invoicing-profiles/overview/__generated__/queries.api-types';
import {type PricingGroupFragment} from 'web-app/react/plans/manage-pricing-groups/__generated__/queries.api-types';
import {type AttendanceSchedulesByIdsQuery} from 'web-app/react/invoicing-profiles/attendance-schedules/__generated__/queries.api-types';

import * as Constants from './constants';

export type PlanTemplate = Plan | null;

export type BillingProfile = GetBillingProfilesByIdsQuery['finance']['billingProfiles']['list'][0];
export type PricingGroup = PricingGroupFragment;
export type AttendanceSchedule = AttendanceSchedulesByIdsQuery['finance']['attendanceSchedules']['list'][0];

export enum BulkChangeMode {
    SCHEDULE_CHANGE_ON_DATE = 'SCHEDULE_CHANGE_ON_DATE',
    UPDATE_PLANS_AFTER_DATE = 'UPDATE_PLANS_AFTER_DATE',
    ADD_NEW_PLAN = 'ADD_NEW_PLAN',
    CHANGE_DISCOUNT = 'CHANGE_DISCOUNT',
    DELETE_LAST_PLAN = 'DELETE_LAST_PLAN',
    UPDATE_BILLING_PROFILE = 'UPDATE_BILLING_PROFILE',
}

export class PlanError extends Record<{
    error: string;
}>({
    error: '',
}) {}

export interface PlanPreviewSuccessState {
    plan: Plan;
    isUpdate?: boolean;
    isCurrent?: boolean;
    hasSaved?: boolean;
}

export interface PlanErrorState extends Omit<PlanPreviewSuccessState, 'plan'> {
    plan?: Plan;
    error: PlanError;
}

export type PlanPreviewState = PlanPreviewSuccessState | PlanErrorState;
export const isPlanPreviewSuccessState = (previewState: PlanPreviewState): previewState is PlanPreviewSuccessState => {
    return Boolean(previewState.plan) && !('error' in previewState);
};
export const isPlanErrorState = (previewState: PlanPreviewState): previewState is PlanErrorState => {
    return 'error' in previewState;
};

export type PlanPreviewStates = List<PlanPreviewState>;

export interface Sorting {
    key: 'childName' | 'childDoB';
    direction: SortDirection;
}

export class ManagePlansState extends Record<{
    currentStep: number;
    showing: boolean;
    sessionsAndProductsFetched: boolean;
    updatingChildren: boolean;
    allPlansFetched: boolean;
    showScheduleChange: boolean;
    childrenToShow: string;
    selectedGroup: string;
    highlightBy: string;
    groups: List<any>;
    childIds: List<string>;
    selectedChildren: OrderedSet<string>;
    selectedChildrenForChange: OrderedSet<string>;
    numberOfChildrenToSave: number;
    numberOfChildrenScheduled: number;
    planPreviews: Map<string, PlanPreviewStates>;
    previewData: any;
    sorting: Sorting;
    childLoading: Map<string, boolean>;
}>({
    currentStep: 0,
    showing: false,
    sessionsAndProductsFetched: false,
    updatingChildren: false,
    allPlansFetched: false,
    showScheduleChange: false,
    childrenToShow: Constants.currentAndUpcoming,
    selectedGroup: '',
    highlightBy: '',
    groups: List(),
    childIds: List(),
    selectedChildren: OrderedSet(),
    selectedChildrenForChange: OrderedSet(),
    numberOfChildrenToSave: 0,
    numberOfChildrenScheduled: 0,
    planPreviews: Map<string, PlanPreviewStates>(),
    previewData: null,
    sorting: {key: 'childName', direction: SortDirection.ASC},
    childLoading: Map<string, boolean>(),
}) {}

export interface PreviewPlansProps {
    referenceIds: string[];
    validFrom?: moment.Moment;
    billingProfileFrom?: {billingProfileId: string};
    billingProfileTo?: {billingProfileId: string};
}

export interface PlanTableInputProps {
    institutionId: string;
    pricingGroupsMap: Map<string, PricingGroup>;
    canChooseAutoPricingGroup: boolean;
    attendanceScheduleMap: Map<string, AttendanceSchedule>;
    billingProfileMap: Map<string, BillingProfile>;
    loadingPricingGroups: boolean;
    sortBy?: (sorting: Sorting) => void;
    sorting?: Sorting;
    hideToolbar?: VoidFunction;
    showPublicFunding: boolean;
    handleFormChange: (formData: ManagePlanFormData) => void;
    loadingDiscountPresets: boolean;
    isSiblingDiscountsEnabled: boolean;
    showModernizedFilters: boolean;
    isFinanceV1: boolean;
}

export enum SiblingDiscountFilterType {
    HasSiblingDiscount = 'HasSiblingDiscount',
    CorrectDiscount = 'CorrectDiscount',
    IncorrectDiscount = 'IncorrectDiscount',
    MissingDiscount = 'MissingDiscount',
}

export interface ManagePlanFormData {
    planActiveOnDate: string | undefined;
    planExpiredByDate: string | undefined;
    childEnrolledOnDate: string | undefined;
    dobRange: {from: string; to: string} | undefined;
    groupId: string;
    highlight: string | null;
    bookingType: string | null;
    bookingId: string | null;
    showCurrentChildren: boolean;
    showFutureChildren: boolean;
    showWithdrawingChildren: boolean;
    showPastChildren: boolean;
    showExpiredPlans: boolean;
    siblingDiscount: SiblingDiscountFilterType | null;
}

export const managePlanFieldNames = nameofFactory<ManagePlanFormData>();
